import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Trash2, Upload, Star, ArrowLeft } from "lucide-react";

export default function AdminProductForm() {
  const { id } = useParams();
  const isNew = !id || id === "novo";
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [cats, setCats] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [p, setP] = useState({
    name: "", sku: "", description: "", price: 0, sale_price: null,
    category_ids: [], sizes: [], colors: [], stock: 0,
    featured: false, best_seller: false, new_arrival: true, active: true,
    images: [],
  });

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data || []));
    if (!isNew) {
      api.get(`/products/${id}`).then((r) => setP(r.data));
    }
  }, [id, isNew]);

  const set = (k) => (e) => {
    const val = e?.target ? e.target.value : e;
    setP((prev) => ({ ...prev, [k]: val }));
  };

  const toggleCat = (cid) => {
    setP((prev) => ({
      ...prev,
      category_ids: prev.category_ids.includes(cid)
        ? prev.category_ids.filter((x) => x !== cid)
        : [...prev.category_ids, cid],
    }));
  };

  const save = async () => {
    const payload = {
      name: p.name,
      sku: p.sku || null,
      description: p.description || "",
      price: Number(p.price) || 0,
      sale_price: p.sale_price ? Number(p.sale_price) : null,
      category_ids: p.category_ids,
      sizes: Array.isArray(p.sizes) ? p.sizes : (p.sizes || "").split(",").map(s => s.trim()).filter(Boolean),
      colors: Array.isArray(p.colors) ? p.colors : (p.colors || "").split(",").map(s => s.trim()).filter(Boolean),
      stock: Number(p.stock) || 0,
      featured: !!p.featured,
      best_seller: !!p.best_seller,
      new_arrival: !!p.new_arrival,
      active: !!p.active,
    };
    try {
      if (isNew) {
        const { data } = await api.post("/admin/products", payload);
        toast.success("Produto criado! Agora adicione fotos.");
        nav(`/admin/produtos/${data.id}`);
      } else {
        await api.put(`/admin/products/${id}`, payload);
        toast.success("Produto atualizado");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    }
  };

  const upload = async (files) => {
    if (isNew) {
      toast.error("Salve o produto primeiro para enviar imagens");
      return;
    }
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    setUploading(true);
    try {
      await api.post(`/admin/products/${id}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get(`/products/${id}`);
      setP(data);
      toast.success("Imagens enviadas");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro no upload");
    } finally {
      setUploading(false);
    }
  };

  const removeImg = async (path) => {
    try {
      await api.delete(`/admin/products/${id}/images`, { params: { path } });
      const { data } = await api.get(`/products/${id}`);
      setP(data);
    } catch {
      toast.error("Erro ao remover");
    }
  };

  const setPrimary = async (path) => {
    try {
      await api.post(`/admin/products/${id}/images/primary`, null, { params: { path } });
      const { data } = await api.get(`/products/${id}`);
      setP(data);
    } catch {
      toast.error("Erro");
    }
  };

  return (
    <div>
      <Link to="/admin/produtos" className="text-xs text-zinc-500 inline-flex items-center gap-2 hover:text-white"><ArrowLeft size={14} /> Voltar</Link>
      <div className="flex items-start justify-between mt-3 mb-8 flex-wrap gap-3">
        <div>
          <div className="overline-label">{isNew ? "Novo" : "Editar"}</div>
          <h1 className="font-display text-4xl text-white tracking-tighter mt-3">{isNew ? "Novo produto" : p.name || "Produto"}</h1>
        </div>
        <Button onClick={save} data-testid="save-product-btn" className="bg-white text-black hover:bg-zinc-200 uppercase tracking-widest text-xs h-11 px-6">Salvar</Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Panel title="Informações básicas">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Nome *" wide><Input data-testid="form-name" value={p.name} onChange={set("name")} className="bg-[#050505] border-white/15 text-white h-11" /></Field>
              <Field label="SKU"><Input data-testid="form-sku" value={p.sku || ""} onChange={set("sku")} placeholder="Gerado automaticamente se vazio" className="bg-[#050505] border-white/15 text-white h-11" /></Field>
              <Field label="Estoque"><Input data-testid="form-stock" type="number" value={p.stock} onChange={set("stock")} className="bg-[#050505] border-white/15 text-white h-11" /></Field>
              <Field label="Descrição" wide><Textarea data-testid="form-desc" value={p.description} onChange={set("description")} rows={5} className="bg-[#050505] border-white/15 text-white" /></Field>
            </div>
          </Panel>

          <Panel title="Preço">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Preço (R$) *"><Input data-testid="form-price" type="number" step="0.01" value={p.price} onChange={set("price")} className="bg-[#050505] border-white/15 text-white h-11" /></Field>
              <Field label="Preço promocional (R$)"><Input data-testid="form-sale" type="number" step="0.01" value={p.sale_price ?? ""} onChange={(e) => setP((prev) => ({ ...prev, sale_price: e.target.value || null }))} className="bg-[#050505] border-white/15 text-white h-11" /></Field>
            </div>
          </Panel>

          <Panel title="Variações">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Tamanhos (separados por vírgula)" wide>
                <Input data-testid="form-sizes" value={Array.isArray(p.sizes) ? p.sizes.join(", ") : p.sizes} onChange={(e) => setP((prev) => ({ ...prev, sizes: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }))} placeholder="P, M, G, GG" className="bg-[#050505] border-white/15 text-white h-11" />
              </Field>
              <Field label="Cores (separadas por vírgula)" wide>
                <Input data-testid="form-colors" value={Array.isArray(p.colors) ? p.colors.join(", ") : p.colors} onChange={(e) => setP((prev) => ({ ...prev, colors: e.target.value.split(",").map(s => s.trim()).filter(Boolean) }))} placeholder="Preto, Branco, Cinza" className="bg-[#050505] border-white/15 text-white h-11" />
              </Field>
            </div>
          </Panel>

          <Panel title="Fotos">
            {isNew ? (
              <p className="text-sm text-zinc-500">Salve o produto para começar a adicionar fotos.</p>
            ) : (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3">
                  {(p.images || []).map((img) => (
                    <div key={img.path} className="relative group aspect-square border border-white/10 bg-black overflow-hidden">
                      <img src={fileUrl(img.path)} alt="" className="w-full h-full object-cover" />
                      {img.is_primary && (
                        <span className="absolute top-1 left-1 text-[10px] bg-white text-black px-1.5 py-0.5">PRIMARY</span>
                      )}
                      <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        {!img.is_primary && (
                          <button onClick={() => setPrimary(img.path)} className="h-8 w-8 bg-white text-black grid place-items-center" title="Definir primária"><Star size={14} /></button>
                        )}
                        <button onClick={() => removeImg(img.path)} className="h-8 w-8 bg-red-600 text-white grid place-items-center" title="Remover"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="upload-images-btn"
                    className="aspect-square border border-dashed border-white/20 hover:border-white text-zinc-400 hover:text-white grid place-items-center transition-colors"
                  >
                    {uploading ? <span className="text-xs">Enviando...</span> : <div className="text-center"><Upload size={16} className="mx-auto mb-1" /><div className="text-xs">Adicionar</div></div>}
                  </button>
                </div>
                <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => e.target.files && upload(e.target.files)} />
              </>
            )}
          </Panel>
        </div>

        <aside className="space-y-6">
          <Panel title="Visibilidade">
            <ToggleRow label="Ativo" value={p.active} onChange={(v) => setP((prev) => ({ ...prev, active: v }))} tid="toggle-active" />
            <ToggleRow label="Destaque" value={p.featured} onChange={(v) => setP((prev) => ({ ...prev, featured: v }))} tid="toggle-featured" />
            <ToggleRow label="Mais vendido" value={p.best_seller} onChange={(v) => setP((prev) => ({ ...prev, best_seller: v }))} tid="toggle-best-seller" />
            <ToggleRow label="Novidade" value={p.new_arrival} onChange={(v) => setP((prev) => ({ ...prev, new_arrival: v }))} tid="toggle-new" />
          </Panel>
          <Panel title="Categorias">
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {cats.map((c) => (
                <label key={c.id} className="flex items-center gap-3 text-sm cursor-pointer">
                  <input type="checkbox" checked={p.category_ids?.includes(c.id)} onChange={() => toggleCat(c.id)} className="accent-white" data-testid={`cat-check-${c.slug}`} />
                  <span className="text-zinc-300">{c.name}</span>
                </label>
              ))}
            </div>
          </Panel>
        </aside>
      </div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <section className="border border-white/10 bg-[#0A0A0C] p-5">
      <div className="overline-label mb-4">{title}</div>
      {children}
    </section>
  );
}
function Field({ label, children, wide }) {
  return <div className={wide ? "sm:col-span-2" : ""}><Label className="text-xs text-zinc-400 mb-2 block">{label}</Label>{children}</div>;
}
function ToggleRow({ label, value, onChange, tid }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
      <span className="text-sm text-zinc-300">{label}</span>
      <Switch checked={value} onCheckedChange={onChange} data-testid={tid} />
    </div>
  );
}
